import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronLeft, X } from 'lucide-react';
import { useAuth } from '@/store/auth';
import { useUi } from '@/store/ui';
import { useCan, useTerms } from '@/hooks/usePermission';
import { cn } from '@/lib/cn';
import { buildNav, visibleNav, type NavItem } from './nav';

export function Logo({ collapsed }: { collapsed?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand text-lg font-bold text-white" aria-hidden>S</span>
      {!collapsed && <span className="text-lg font-semibold tracking-tight text-ink">StopLord</span>}
    </div>
  );
}

/** Links with a query string (e.g. ?tab=pf) are active only when that query matches too. */
function useIsActive() {
  const loc = useLocation();
  return (it: NavItem) => {
    const [path, query] = it.to.split('?');
    const pathMatch = it.end || query ? loc.pathname === path : loc.pathname === path || loc.pathname.startsWith(`${path}/`);
    if (!pathMatch) return false;
    if (!query) return !new URLSearchParams(loc.search).get('tab') || loc.pathname !== '/payroll/settings';
    const want = new URLSearchParams(query);
    const have = new URLSearchParams(loc.search);
    return [...want.entries()].every(([k, v]) => (have.get(k) ?? (k === 'tab' ? 'structures' : '')) === v);
  };
}

const linkClass = (active: boolean, collapsed: boolean, nested = false) =>
  cn(
    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
    nested && !collapsed && 'py-1.5 pl-9 text-[13px]',
    collapsed && 'justify-center px-0',
    active ? 'bg-brand text-white shadow-sm' : 'text-ink-muted hover:bg-surface-hover hover:text-ink',
  );

function NavEntry({ it, collapsed, onNavigate, nested }: { it: NavItem; collapsed: boolean; onNavigate?: () => void; nested?: boolean }) {
  const isActive = useIsActive();
  return (
    <li>
      <NavLink to={it.to} end={it.end} onClick={onNavigate} title={collapsed ? it.label : undefined} className={() => linkClass(isActive(it), collapsed, nested)} aria-current={isActive(it) ? 'page' : undefined}>
        {it.icon}
        {!collapsed && <span className="truncate">{it.label}</span>}
      </NavLink>
    </li>
  );
}

function NavSection({ it, collapsed, onNavigate }: { it: NavItem; collapsed: boolean; onNavigate?: () => void }) {
  const isActive = useIsActive();
  const childActive = it.children!.some((c) => isActive(c));
  const [open, setOpen] = useState(childActive);
  const expanded = open || childActive;
  if (collapsed) return <>{it.children!.map((c) => <NavEntry key={c.to + c.label} it={c} collapsed onNavigate={onNavigate} />)}</>;
  const id = `nav-${it.label.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <li>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={expanded} aria-controls={id} className={cn('flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors', childActive ? 'text-ink' : 'text-ink-muted hover:bg-surface-hover hover:text-ink')}>
        {it.icon}
        <span className="flex-1 truncate text-left">{it.label}</span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform', expanded && 'rotate-180')} aria-hidden />
      </button>
      {expanded && <ul id={id} className="mt-0.5 space-y-0.5">{it.children!.map((c) => <NavEntry key={c.to + c.label} it={c} collapsed={false} onNavigate={onNavigate} nested />)}</ul>}
    </li>
  );
}

function NavContent({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const can = useCan();
  const terms = useTerms();
  const tracks = useAuth((s) => !!s.user?.tracksWorkTime);
  const groups = visibleNav(buildNav(terms), can, tracks);
  return (
    <nav aria-label="Main" className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
      {groups.map((g) => (
        <div key={g.label}>
          {!collapsed && <p className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-ink-subtle">{g.label}</p>}
          <ul className="space-y-0.5">
            {g.items.map((it) => (it.children ? <NavSection key={it.label} it={it} collapsed={collapsed} onNavigate={onNavigate} /> : <NavEntry key={it.to + it.label} it={it} collapsed={collapsed} onNavigate={onNavigate} />))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar, mobileNavOpen, setMobileNav } = useUi();
  const tenantName = useAuth((s) => s.tenant?.name);
  return (
    <>
      <aside className={cn('sticky top-0 hidden h-dvh shrink-0 flex-col border-r border-line bg-surface transition-[width] lg:flex', sidebarCollapsed ? 'w-[76px]' : 'w-64')}>
        <div className={cn('flex h-16 items-center border-b border-line px-5', sidebarCollapsed && 'justify-center px-0')}>
          <Logo collapsed={sidebarCollapsed} />
        </div>
        {!sidebarCollapsed && tenantName && <p className="truncate px-6 pt-3 text-xs text-ink-subtle" title={tenantName}>{tenantName}</p>}
        <NavContent collapsed={sidebarCollapsed} />
        <button type="button" onClick={toggleSidebar} className="m-3 flex items-center justify-center gap-2 rounded-lg py-2 text-xs text-ink-subtle hover:bg-surface-hover hover:text-ink" aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <ChevronLeft className={cn('h-4 w-4 transition-transform', sidebarCollapsed && 'rotate-180')} />
          {!sidebarCollapsed && 'Collapse'}
        </button>
      </aside>

      {/* Mobile drawer */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
          <div className="absolute inset-0 bg-slate-950/45" onClick={() => setMobileNav(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-surface shadow-xl">
            <div className="flex h-16 items-center justify-between border-b border-line px-5">
              <Logo />
              <button type="button" onClick={() => setMobileNav(false)} className="rounded-lg p-2 text-ink-muted hover:bg-surface-hover" aria-label="Close navigation"><X className="h-5 w-5" /></button>
            </div>
            <NavContent collapsed={false} onNavigate={() => setMobileNav(false)} />
          </aside>
        </div>
      )}
    </>
  );
}
